import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Form, FormGroup, Label, Input, Modal, ModalBody, ModalHeader, Row, Col, Card } from "reactstrap";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const CATEGORIES = [
  { value: "partner_notification", label: "Partner Notification" },
  { value: "client_confirmation", label: "Client Confirmation" },
  { value: "system", label: "System" },
  { value: "other", label: "Other" },
];

/** Default templates that can be installed (name must match backend DEFAULT_TEMPLATES) */
const DEFAULT_TEMPLATE_OPTIONS = [
  { name: "partner_quote_notification", label: "Partner quote notification" },
  { name: "welcome", label: "Welcome / test" },
  { name: "quote_ack", label: "Quote acknowledgment" },
  { name: "quote_ack_new_user", label: "Quote ack (new user)" },
  { name: "reset_password", label: "Reset password" },
  { name: "teleconsultation_confirmation", label: "Teleconsultation – patient confirmation" },
  { name: "teleconsultation_reminder", label: "Teleconsultation – reminder" },
];

const DEFAULT_FORM = {
  name: "",
  description: "",
  subject: "",
  htmlContent: "",
  textContent: "",
  variables: [],
  category: "partner_notification",
  isActive: true,
};

const DEFAULT_PREVIEW_DATA = {
  partnerName: "Test Medical Partner",
  patientFirstName: "John",
  patientLastName: "Doe",
  treatment: "Dental Implant",
  destination: "Turkey",
  sourceCountry: "United States",
  budget: "$10,000 - $15,000",
  quoteId: "507f1f77bcf86cd799439001",
  dashboardLink: "https://admin.inoqare.com/partners/dashboard",
};

/** Default template variable names (partner notification, etc.) */
const DEFAULT_VARIABLE_NAMES = [
  "partnerName",
  "clinicName",
  "quoteId",
  "patientFirstName",
  "patientLastName",
  "patientEmail",
  "patientCountryCode",
  "patientPhone",
  "treatmentInfo",
  "sourceCountry",
  "destinationCountry",
  "budget",
  "dashboardLink",
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

const EmailTemplatesManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [, setPagination] = useState({ total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [modalEdit, setModalEdit] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [logsTab, setLogsTab] = useState(false);
  const [logs, setLogs] = useState([]);
  const [, setLogsPagination] = useState({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [selectedSeedTemplates, setSelectedSeedTemplates] = useState(() => DEFAULT_TEMPLATE_OPTIONS.map((t) => t.name));
  const [variablesTab, setVariablesTab] = useState(false);
  const [variables, setVariables] = useState([]);
  const [variablesLoading, setVariablesLoading] = useState(false);
  const [variableModal, setVariableModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [variableForm, setVariableForm] = useState({ name: "", description: "", example: "" });
  const [seedVariablesLoading, setSeedVariablesLoading] = useState(false);
  const quillRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter === "active") params.append("isActive", "true");
      if (filter === "inactive") params.append("isActive", "false");
      if (category !== "all") params.append("category", category);
      const res = await http.get(`/api/v1/email-templates?${params.toString()}`);
      const data = res.data.data;
      setTemplates(data.docs || []);
      setPagination(data.pagination || { total: 0, pages: 0 });
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to load templates", "error");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [filter, category]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchLogs = useCallback(async () => {
    if (!logsTab) return;
    try {
      setLogsLoading(true);
      const res = await http.get("/api/v1/email-templates/logs?limit=50");
      const data = res.data.data;
      setLogs(data.docs || []);
      setLogsPagination(data.pagination || {});
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to load logs", "error");
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [logsTab]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const fetchVariables = useCallback(async () => {
    try {
      setVariablesLoading(true);
      const res = await http.get("/api/v1/email-templates/variables");
      setVariables(res.data?.data || []);
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to load variables", "error");
      setVariables([]);
    } finally {
      setVariablesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVariables();
  }, [fetchVariables]);

  useEffect(() => {
    if (variablesTab) fetchVariables();
  }, [variablesTab, fetchVariables]);

  const filteredTemplates = templates.filter((t) => {
    if (filter === "active" && !t.isActive) return false;
    if (filter === "inactive" && t.isActive) return false;
    if (category !== "all" && t.category !== category) return false;
    return true;
  });

  const openAdd = () => {
    setEditingTemplate(null);
    setFormData({ ...DEFAULT_FORM });
    setModalEdit(true);
  };

  const openEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      subject: template.subject,
      htmlContent: template.htmlContent,
      textContent: template.textContent || "",
      variables: Array.isArray(template.variables) ? [...template.variables] : [],
      category: template.category,
      isActive: template.isActive,
    });
    setModalEdit(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await http.put(`/api/v1/email-templates/${editingTemplate._id}`, formData);
        swal("Success", "Template updated", "success");
      } else {
        await http.post("/api/v1/email-templates", formData);
        swal("Success", "Template created", "success");
      }
      setModalEdit(false);
      fetchTemplates();
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to save template", "error");
    }
  };

  const handleDelete = (template) => {
    swal({
      title: "Delete template?",
      text: `"${template.name}" will be permanently deleted.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        http
          .delete(`/api/v1/email-templates/${template._id}`)
          .then(() => {
            swal("Deleted", "Template has been deleted", "success");
            fetchTemplates();
          })
          .catch((err) => {
            swal("Error", err.response?.data?.error || "Failed to delete", "error");
          });
      }
    });
  };

  const handlePreview = async (template) => {
    try {
      const res = await http.post(`/api/v1/email-templates/${template._id}/preview`, {
        testData: DEFAULT_PREVIEW_DATA,
      });
      setPreviewData(res.data.data);
      setPreviewModal(true);
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to preview", "error");
    }
  };

  const handleSeedDefaults = () => {
    const toInstall = selectedSeedTemplates.length ? selectedSeedTemplates : DEFAULT_TEMPLATE_OPTIONS.map((t) => t.name);
    swal({
      title: "Install selected templates?",
      text: toInstall.length === DEFAULT_TEMPLATE_OPTIONS.length
        ? "Selected default templates will be created if missing. You can then edit them in the editor."
        : `${toInstall.length} template(s) will be created if missing.`,
      icon: "info",
      buttons: ["Cancel", "Install"],
    }).then((ok) => {
      if (ok) {
        setSeedLoading(true);
        http
          .post("/api/v1/email-templates/seed-defaults", { templateNames: toInstall })
          .then((res) => {
            const created = res.data?.data?.created || [];
            const msg = created.length
              ? `Created: ${created.join(", ")}.`
              : "Selected templates already exist or none selected.";
            swal("Done", msg, "success");
            fetchTemplates();
          })
          .catch((err) => {
            swal("Error", err.response?.data?.error || "Failed to install templates.", "error");
          })
          .finally(() => setSeedLoading(false));
      }
    });
  };

  const toggleSeedTemplate = (name) => {
    setSelectedSeedTemplates((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const selectAllSeedTemplates = () => setSelectedSeedTemplates(DEFAULT_TEMPLATE_OPTIONS.map((t) => t.name));
  const deselectAllSeedTemplates = () => setSelectedSeedTemplates([]);

  const handleSeedVariables = () => {
    setSeedVariablesLoading(true);
    http
      .post("/api/v1/email-templates/variables/seed-defaults")
      .then((res) => {
        const msg = res.data?.data?.created?.length ? "Default variable(s) created." : "All default variables already exist.";
        swal("Done", msg, "success");
        fetchVariables();
      })
      .catch((err) => swal("Error", err.response?.data?.error || "Failed to seed variables", "error"))
      .finally(() => setSeedVariablesLoading(false));
  };

  const openAddVariable = () => {
    setEditingVariable(null);
    setVariableForm({ name: "", description: "", example: "" });
    setVariableModal(true);
  };

  const openEditVariable = (v) => {
    setEditingVariable(v);
    setVariableForm({ name: v.name, description: v.description || "", example: v.example || "" });
    setVariableModal(true);
  };

  const handleSaveVariable = async (e) => {
    e.preventDefault();
    if (!variableForm.name.trim()) {
      swal("Error", "Variable name is required.", "error");
      return;
    }
    try {
      if (editingVariable) {
        await http.put(`/api/v1/email-templates/variables/${editingVariable._id}`, variableForm);
        swal("Success", "Variable updated.", "success");
      } else {
        await http.post("/api/v1/email-templates/variables", variableForm);
        swal("Success", "Variable created.", "success");
      }
      setVariableModal(false);
      fetchVariables();
    } catch (err) {
      swal("Error", err.response?.data?.error || "Failed to save variable", "error");
    }
  };

  const handleDeleteVariable = (v) => {
    swal({
      title: "Delete variable?",
      text: `"{{${v.name}}}" will be removed from the list. Templates using it will keep the placeholder.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((ok) => {
      if (ok) {
        http
          .delete(`/api/v1/email-templates/variables/${v._id}`)
          .then(() => {
            swal("Deleted", "Variable removed.", "success");
            fetchVariables();
          })
          .catch((err) => swal("Error", err.response?.data?.error || "Failed to delete", "error"));
      }
    });
  };

  const addVariable = () => {
    setFormData({
      ...formData,
      variables: [...formData.variables, { name: "", description: "", example: "" }],
    });
  };

  const removeVariable = (index) => {
    setFormData({
      ...formData,
      variables: formData.variables.filter((_, i) => i !== index),
    });
  };

  const updateVariable = (index, field, value) => {
    const next = [...formData.variables];
    next[index] = { ...next[index], [field]: value };
    setFormData({ ...formData, variables: next });
  };

  const insertVariableIntoEditor = (varName) => {
    const token = `{{${varName}}}`;
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, token);
      editor.setSelection(index + token.length);
    } else {
      setFormData((prev) => ({ ...prev, htmlContent: (prev.htmlContent || "") + token }));
    }
  };

  const variableOptionsForEditor = [
    ...new Set([
      ...DEFAULT_VARIABLE_NAMES,
      ...(variables || []).map((v) => v.name).filter(Boolean),
      ...(formData.variables || []).map((v) => v.name).filter(Boolean),
    ]),
  ].filter(Boolean);

  return (
    <React.Fragment>
      <Head title="Email Templates" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">Email Templates</BlockTitle>
              <BlockDes>
                <p>
                  Manage templates, variables, and send logs. Outbound delivery (Gmail OAuth, sender, test send) lives in{" "}
                  <Link to={`${getPublicUrl("admin-settings")}?tab=email`}>Settings → Email delivery</Link> — one place for configuration.
                </p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="primary" onClick={openAdd}>
                <Icon name="plus" />
                <span>Add template</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <ul className="nav nav-tabs nav-tabs-s1">
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${!logsTab && !variablesTab ? "active" : ""}`}
                    onClick={() => { setLogsTab(false); setVariablesTab(false); }}
                  >
                    Templates
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${variablesTab ? "active" : ""}`}
                    onClick={() => { setLogsTab(false); setVariablesTab(true); }}
                  >
                    Variables
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${logsTab ? "active" : ""}`}
                    onClick={() => { setLogsTab(true); setVariablesTab(false); }}
                  >
                    Logs
                  </button>
                </li>
              </ul>

              {variablesTab ? (
                <div className="mt-4">
                  {variablesLoading ? (
                    <p className="text-soft">Loading variables...</p>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <p className="text-soft small mb-0">Variables available for use in templates as {`{{name}}`}. Use the dropdown in the template editor to insert them.</p>
                        <div>
                          <Button color="light" size="sm" className="mr-2" onClick={handleSeedVariables} disabled={seedVariablesLoading}>
                            {seedVariablesLoading ? "Seeding..." : "Seed default variables"}
                          </Button>
                          <Button color="primary" size="sm" onClick={openAddVariable}>
                            <Icon name="plus" /> Add variable
                          </Button>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                              <th>Example</th>
                              <th className="text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {variables.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-soft text-center py-4">No variables. Seed defaults or add one.</td>
                              </tr>
                            ) : (
                              variables.map((v) => (
                                <tr key={v._id}>
                                  <td><code>{`{{${v.name}}}`}</code></td>
                                  <td>{v.description || "—"}</td>
                                  <td>{v.example || "—"}</td>
                                  <td className="text-right">
                                    <Button color="primary" size="sm" className="mr-1" onClick={() => openEditVariable(v)}>Edit</Button>
                                    <Button color="danger" outline size="sm" onClick={() => handleDeleteVariable(v)}>Delete</Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              ) : !logsTab ? (
                <>
                  <Card className="card-bordered mb-4 bg-light">
                    <div className="card-inner">
                      <h6 className="card-title mb-2">Default templates</h6>
                      <p className="text-soft small mb-3">
                        Select one or more templates to install (created only if missing). Includes partner notification, quote ack, reset password, and <strong>teleconsultation</strong> (patient confirmation, reminder). You can then edit them in the editor.
                      </p>
                      <div className="mb-3">
                        {DEFAULT_TEMPLATE_OPTIONS.map((opt) => (
                          <div key={opt.name} className="form-check form-check-sm">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`seed-${opt.name}`}
                              checked={selectedSeedTemplates.includes(opt.name)}
                              onChange={() => toggleSeedTemplate(opt.name)}
                            />
                            <label className="form-check-label" htmlFor={`seed-${opt.name}`}>
                              {opt.label} ({opt.name})
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex flex-wrap flex-g-2 align-items-center">
                        <Button color="primary" size="sm" onClick={handleSeedDefaults} disabled={seedLoading || selectedSeedTemplates.length === 0}>
                          {seedLoading ? "Installing..." : "Install selected templates"}
                        </Button>
                        <Button color="light" size="sm" onClick={selectAllSeedTemplates}>Select all</Button>
                        <Button color="light" size="sm" onClick={deselectAllSeedTemplates}>Deselect all</Button>
                      </div>
                    </div>
                  </Card>
                  <Row className="mt-2">
                    <Col md="3">
                      <FormGroup>
                        <Label>Status</Label>
                        <Input
                          type="select"
                          value={filter}
                          onChange={(e) => setFilter(e.target.value)}
                        >
                          <option value="all">All</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label>Category</Label>
                        <Input
                          type="select"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                        >
                          <option value="all">All</option>
                          {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {loading ? (
                    <p className="text-soft">Loading...</p>
                  ) : filteredTemplates.length === 0 ? (
                    <p className="text-soft">No templates found.</p>
                  ) : (
                    <div className="row mt-3">
                      {filteredTemplates.map((template) => (
                        <div key={template._id} className="col-md-6 col-lg-4 mb-4">
                          <Card className="card-bordered h-100">
                            <div className="card-inner">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0">{template.name}</h6>
                                <span className={`badge badge-dot badge-${template.isActive ? "success" : "secondary"}`}>
                                  {template.isActive ? "Active" : "Inactive"}
                                </span>
                              </div>
                              <span className="badge badge-outline-primary mb-2">{template.category}</span>
                              {template.description && (
                                <p className="text-soft small mb-2">{template.description}</p>
                              )}
                              <p className="small mb-3">
                                <strong>Subject:</strong> {template.subject}
                              </p>
                              <div className="d-flex flex-wrap flex-g-1">
                                <Button size="sm" color="light" onClick={() => handlePreview(template)}>
                                  Preview
                                </Button>
                                <Button size="sm" color="primary" onClick={() => openEdit(template)}>
                                  Edit
                                </Button>
                                <Button size="sm" color="danger" outline onClick={() => handleDelete(template)}>
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </Card>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-4">
                  {logsLoading ? (
                    <p className="text-soft">Loading logs...</p>
                  ) : logs.length === 0 ? (
                    <p className="text-soft">No email logs.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>To</th>
                            <th>Template</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log) => (
                            <tr key={log._id}>
                              <td>{log.createdAt ? new Date(log.createdAt).toLocaleString() : "-"}</td>
                              <td>{log.to || "-"}</td>
                              <td>{log.template?.name || "-"}</td>
                              <td>
                                <span className={`badge badge-${log.status === "sent" ? "success" : log.status === "failed" ? "danger" : "secondary"}`}>
                                  {log.status || "-"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Block>

        <Modal isOpen={modalEdit} toggle={() => setModalEdit(false)} size="xl">
          <ModalHeader toggle={() => setModalEdit(false)}>
            {editingTemplate ? "Edit Template" : "Create Template"}
          </ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g. partner_quote_notification"
                />
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                />
              </FormGroup>
              <FormGroup>
                <Label>Category *</Label>
                <Input
                  type="select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Subject *</Label>
                <Input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="Use {{variableName}} for variables"
                />
              </FormGroup>
              <FormGroup>
                <Label>HTML content *</Label>
                <div className="mb-2 d-flex align-items-center flex-wrap flex-g-2">
                  <span className="text-soft small">Insert variable:</span>
                  <Input
                    type="select"
                    className="form-control form-control-sm w-auto"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        insertVariableIntoEditor(val);
                        e.target.value = "";
                      }
                    }}
                  >
                    <option value="">-- Choose --</option>
                    {variableOptionsForEditor.map((name) => (
                      <option key={name} value={name}>
                        {`{{${name}}}`}
                      </option>
                    ))}
                  </Input>
                </div>
                <div className="email-template-editor-wrap">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={formData.htmlContent || ""}
                    onChange={(content) => setFormData({ ...formData, htmlContent: content })}
                    modules={QUILL_MODULES}
                    placeholder="Email content. Use the menu above to insert variables {{name}}."
                    style={{ minHeight: "280px" }}
                  />
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Text Content (optional)</Label>
                <Input
                  type="textarea"
                  value={formData.textContent}
                  onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                  rows={3}
                  placeholder="Plain text version"
                />
              </FormGroup>
              <FormGroup>
                <Label>Variables</Label>
                {formData.variables.map((v, i) => (
                  <Row key={i} className="mb-2">
                    <Col md="4">
                      <Input
                        placeholder="Name"
                        value={v.name}
                        onChange={(e) => updateVariable(i, "name", e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <Input
                        placeholder="Description"
                        value={v.description}
                        onChange={(e) => updateVariable(i, "description", e.target.value)}
                      />
                    </Col>
                    <Col md="3">
                      <Input
                        placeholder="Example"
                        value={v.example}
                        onChange={(e) => updateVariable(i, "example", e.target.value)}
                      />
                    </Col>
                    <Col md="1">
                      <Button color="danger" outline size="sm" onClick={() => removeVariable(i)}>
                        <Icon name="trash" />
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button type="button" color="light" size="sm" onClick={addVariable}>
                  <Icon name="plus" /> Add variable
                </Button>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  Active
                </Label>
              </FormGroup>
              <div className="mt-3">
                <Button color="primary" type="submit">
                  {editingTemplate ? "Update" : "Create"}
                </Button>
                <Button color="light" className="ml-2" onClick={() => setModalEdit(false)}>
                  Cancel
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal isOpen={previewModal} toggle={() => setPreviewModal(false)} size="lg">
          <ModalHeader toggle={() => setPreviewModal(false)}>Email Preview</ModalHeader>
          <ModalBody>
            {previewData && (
              <>
                <p className="mb-2">
                  <strong>Subject:</strong> {previewData.subject}
                </p>
                <div
                  className="border rounded p-3 bg-white"
                  style={{ maxHeight: "70vh", overflow: "auto" }}
                  dangerouslySetInnerHTML={{ __html: previewData.htmlContent || "" }}
                />
              </>
            )}
          </ModalBody>
        </Modal>

        <Modal isOpen={variableModal} toggle={() => setVariableModal(false)}>
          <ModalHeader toggle={() => setVariableModal(false)}>{editingVariable ? "Edit variable" : "Add variable"}</ModalHeader>
          <ModalBody>
            <Form onSubmit={handleSaveVariable}>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  type="text"
                  value={variableForm.name}
                  onChange={(e) => setVariableForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. partnerName"
                  required
                  disabled={!!editingVariable}
                />
                {editingVariable && <span className="form-note small text-soft">Name cannot be changed when editing.</span>}
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="text"
                  value={variableForm.description}
                  onChange={(e) => setVariableForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="e.g. Partner contact name"
                />
              </FormGroup>
              <FormGroup>
                <Label>Example</Label>
                <Input
                  type="text"
                  value={variableForm.example}
                  onChange={(e) => setVariableForm((p) => ({ ...p, example: e.target.value }))}
                  placeholder="e.g. Dr. Smith"
                />
              </FormGroup>
              <div className="d-flex flex-g-2">
                <Button color="primary" type="submit">{editingVariable ? "Update" : "Create"}</Button>
                <Button color="light" type="button" onClick={() => setVariableModal(false)}>Cancel</Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default EmailTemplatesManagement;
